/* SORAYA product page — cart wiring + the two animations not covered by the
   theme's own scripts (marquee tickers and section scroll-reveal).
   The ticking timer, "sold" counter, stats count-up and testimonials slider
   come from the page's own initializers in hematite-anim.js. */
(function () {
  // Marquee speed: scale duration to each ticker's content width for an even pace.
  function marquees() {
    document.querySelectorAll('.horizontal-ticker__container').forEach((c) => {
      const w = c.scrollWidth || 2000;
      c.style.setProperty('--hx-ticker-dur', Math.max(20, Math.round(w / 90)) + 's');
    });
  }

  // Motion only while on screen: videos load + play near the viewport, and
  // marquee tickers pause off screen so they don't burn paint time.
  function lazyMotion() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('video[data-src]').forEach((v) => { v.src = v.dataset.src; v.play().catch(() => {}); });
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const el = e.target;
        if (el.tagName === 'VIDEO') {
          if (e.isIntersecting) {
            if (!el.src) el.src = el.dataset.src;
            el.play().catch(() => {});
          } else if (el.src) el.pause();
        } else if (el.classList.contains('horizontal-ticker__container')) {
          el.style.animationPlayState = e.isIntersecting ? '' : 'paused';
        } else {
          // SVG waves / pulsing dots: their animations can't run on the compositor.
          el.classList.toggle('hx-offscreen', !e.isIntersecting);
        }
      });
    }, { rootMargin: '200px 0px' });
    document.querySelectorAll('video[data-src], .horizontal-ticker__container, .waves-animated-1').forEach((el) => io.observe(el));
    document.querySelectorAll('.notice-dot, .low-stock-banner__dot').forEach((el) => io.observe(el.parentElement));
  }

  // Scroll-reveal: fade/slide each animated section in as it enters the viewport.
  // Bulletproof: an observer, a scroll fallback, and a time fallback so a section
  // can never stay hidden.
  function reveal() {
    if (window.matchMedia('(prefers-reduced-motion:reduce)').matches) return;
    const hidden = [];
    document.querySelectorAll('.animate-section').forEach((t) => {
      const r = t.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) return; // in view on load → leave shown
      t.classList.add('hx-reveal');
      hidden.push(t);
    });
    if (!hidden.length) return;
    const show = (t) => { t.classList.add('hx-in'); };
    const sweep = () => {
      for (let i = hidden.length - 1; i >= 0; i--) {
        const r = hidden[i].getBoundingClientRect();
        if (r.top < window.innerHeight * 0.95) { show(hidden[i]); hidden.splice(i, 1); }
      }
      if (!hidden.length) window.removeEventListener('scroll', sweep);
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { show(e.target); io.unobserve(e.target); const i = hidden.indexOf(e.target); if (i >= 0) hidden.splice(i, 1); } });
      }, { threshold: 0, rootMargin: '0px 0px -5% 0px' });
      hidden.slice().forEach((t) => io.observe(t));
    }
    window.addEventListener('scroll', sweep, { passive: true });
    sweep();
    // Safety net: never leave anything hidden.
    setTimeout(() => hidden.slice().forEach(show), 5000);
  }

  // Functional cart: map the selected bundle bar to a Soraya variant, add, checkout.
  function wireCart() {
    const root = document.querySelector('#MainContent') || document;
    root.querySelectorAll('.kaching-bundles__bar[data-soraya-variant]').forEach((bar) => {
      bar.addEventListener('click', () => {
        root.querySelectorAll('.kaching-bundles__bar').forEach((b) => b.classList.remove('kaching-bundles__bar--selected'));
        bar.classList.add('kaching-bundles__bar--selected');
      });
    });
    function variant() {
      const sel = root.querySelector('.kaching-bundles__bar--selected[data-soraya-variant]')
        || root.querySelector('[data-soraya-default]')
        || root.querySelector('[data-soraya-variant]');
      return sel ? sel.getAttribute('data-soraya-variant') : null;
    }
    async function add(e) {
      if (e) e.preventDefault();
      const id = variant();
      if (!id) return;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
        });
        if (!res.ok) throw new Error('add failed');
        window.location.href = '/checkout';
      } catch (err) {
        window.location.href = '/cart/' + id + ':1';
      }
    }
    root.querySelectorAll('form[action*="/cart/add"], product-form form').forEach((f) => f.addEventListener('submit', add));
    root.querySelectorAll('[data-soraya-atc]').forEach((b) => b.addEventListener('click', add));
  }

  function wireGallery() {
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      const slides = Array.from(gallery.querySelectorAll('.product__media-item'));
      const thumbnails = Array.from(gallery.querySelectorAll('.thumbnail-list__item'));
      if (!slides.length) return;

      let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let lastTrigger = null;

      const setActive = (index, shouldScroll = true) => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === active));
        thumbnails.forEach((item, i) => {
          const button = item.querySelector('button');
          if (!button) return;
          if (i === active) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
        if (shouldScroll) {
          const track = gallery.querySelector('#Slider-Gallery-' + gallery.id.replace('MediaGallery-', ''));
          // The track is a horizontal scroller on desktop too, not just mobile.
          if (track) {
            const pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
            const left = track.scrollLeft + slides[active].getBoundingClientRect().left - track.getBoundingClientRect().left - pad;
            track.scrollTo({ left, behavior: 'smooth' });
          }
        }
      };

      thumbnails.forEach((item, index) => {
        const button = item.querySelector('button');
        if (button) button.addEventListener('click', (event) => {
          event.preventDefault();
          setActive(index);
        });
      });

      const lightbox = document.createElement('div');
      lightbox.className = 'product-image-lightbox';
      lightbox.setAttribute('role', 'dialog');
      lightbox.setAttribute('aria-modal', 'true');
      lightbox.setAttribute('aria-label', 'Product image gallery');
      lightbox.innerHTML = '<button class="product-image-lightbox__close" type="button" aria-label="Close image">&times;</button><button class="product-image-lightbox__previous" type="button" aria-label="Previous image">&#8249;</button><img class="product-image-lightbox__image" alt=""><button class="product-image-lightbox__next" type="button" aria-label="Next image">&#8250;</button><span class="product-image-lightbox__count" aria-live="polite"></span>';
      document.body.appendChild(lightbox);
      const lightboxImage = lightbox.querySelector('.product-image-lightbox__image');
      const count = lightbox.querySelector('.product-image-lightbox__count');
      const renderLightbox = () => {
        const image = slides[active].querySelector('.product__media img');
        if (!image) return;
        lightboxImage.src = image.currentSrc || image.src;
        lightboxImage.alt = image.alt || 'Product image';
        count.textContent = (active + 1) + ' / ' + slides.length;
      };
      const openLightbox = (trigger) => {
        lastTrigger = trigger;
        renderLightbox();
        lightbox.classList.add('is-open');
        document.body.classList.add('product-image-lightbox-open');
        lightbox.querySelector('.product-image-lightbox__close').focus();
      };
      const closeLightbox = () => {
        lightbox.classList.remove('is-open');
        document.body.classList.remove('product-image-lightbox-open');
        if (lastTrigger) lastTrigger.focus();
      };

      slides.forEach((slide) => {
        const image = slide.querySelector('.product__media img');
        if (!image) return;
        image.style.cursor = 'zoom-in';
        image.addEventListener('click', () => openLightbox(image));
      });
      lightbox.querySelector('.product-image-lightbox__close').addEventListener('click', closeLightbox);
      lightbox.querySelector('.product-image-lightbox__previous').addEventListener('click', () => { setActive(active - 1, false); renderLightbox(); });
      lightbox.querySelector('.product-image-lightbox__next').addEventListener('click', () => { setActive(active + 1, false); renderLightbox(); });
      lightbox.addEventListener('click', (event) => { if (event.target === lightbox) closeLightbox(); });
      document.addEventListener('keydown', (event) => {
        if (!lightbox.classList.contains('is-open')) return;
        if (event.key === 'Escape') closeLightbox();
        if (event.key === 'ArrowLeft') { setActive(active - 1, false); renderLightbox(); }
        if (event.key === 'ArrowRight') { setActive(active + 1, false); renderLightbox(); }
      });
      setActive(active, false);
    });
  }

  // Sticky add-to-cart bar: the theme's <sticky-atc> script isn't loaded, so slide it
  // in once the main ATC button scrolls above the viewport, and out near the footer.
  function stickyAtc() {
    const bar = document.querySelector('sticky-atc');
    const atc = document.querySelector('.main-product-atc');
    if (!bar || !atc) return;
    document.body.appendChild(bar); // escape ancestors whose filter/transform break position:fixed
    const footer = document.querySelector('footer');
    let pastAtc = false, atFooter = false;
    const update = () => bar.classList.toggle('hx-shown', pastAtc && !atFooter);
    new IntersectionObserver(([e]) => { pastAtc = !e.isIntersecting && e.boundingClientRect.top < 0; update(); }).observe(atc);
    if (footer) new IntersectionObserver(([e]) => { atFooter = e.isIntersecting; update(); }).observe(footer);
    const btn = bar.querySelector('.sticky-atc__scroll-btn');
    const target = document.querySelector('kaching-bundle') || atc;
    if (btn) btn.addEventListener('click', (e) => {
      e.preventDefault();
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
    });
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }
  ready(() => { marquees(); lazyMotion(); reveal(); wireCart(); wireGallery(); stickyAtc(); });
})();
